/* eslint-disable react/display-name */
import React, { useRef, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import styled from '@emotion/styled'
import { useDispatch } from 'react-redux'
import { useSelector } from 'hooks/redux'
import {
  setSelectedIdWithDrawer,
  setComponentMeta,
  removeComponentMeta,
} from 'actions/navigator'
import { useMakeDispatchCallback } from 'hooks'
import {
  QUERY_TYPES_WITH_EVALUATION,
  CARRY_CONNECTOR_TYPES,
  SIMPLE,
  CAN_INPUT,
  COMPLEX,
  NO_EDGE,
  CARRY_TIMER,
} from 'utils/constants'
import strings from 'utils/strings'
import CarryTo from 'components/feature/CarryTo'
import NodeConnector from 'components/feature/NodeConnector'
import { FeatureWarningIconStyled } from 'components/patterns/Icon/IconsStyled'
import { classNames } from 'utils/utils'
import {
  Icon__Swap as IconSwap,
  Icon__Alert as IconAlert,
} from 'components/patterns/Icon/components'
import { Menu } from 'antd'
import { MenuItem } from 'components/patterns/DropdownMenu/DropdownMenu.style'
import {
  StyledCricleDiv,
  StyledDropdown,
} from 'components/feature/NodeConnector/NodeConnector.style'
import { Tooltip, Header } from 'components/patterns'
import {
  ModalSmall,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalContent,
  ModalMainContainer,
  ModalCloseButton,
  ModalButton,
} from 'components/patterns/Modal'
import {
  moveSingleCondition,
  moveSingleConditionToLogicSet,
} from 'actions/feature/conditions'
import {
  getLogicNode,
  makeGetNewLogicNodeIndex,
} from 'selectors/feature/logicNodes'
import withStyle, {
  StyledEvaluationBadge,
  StyledRelationSymbol,
  StyledRungItem,
  StyledConditionLabel,
} from './Condition.style'
import QuickAddLogicMenu, { StyledMenuWrapper } from '../QuickAddLogicMenu'

const getNewLogicSetIndex = makeGetNewLogicNodeIndex(COMPLEX)

const Condition = (props) => {
  const {
    canId,
    onCarryIconClick,
    rungType,
    affinityGroupNumber,
    highlightAffinity,
    className,
    parentQueryUri,
    inputUri,
    fnId,
    uri,
    isSelected,
    isSixteenBitCanOutput,
    selectedId,
    label,
    isLink,
    vertical,
    ignoreCarry,
    hysteresis,
    selectedInputDescription,
    operator,
    value,
    edge,
    inputConfig,
    translationType,
    translations,
    units,
    hasErrors,
    isSingleCondition,
    isLast,
    isFirst,
    isOnly,
    carryToType,
    carryType,
    result,
    parentType,
    subType,
    evaluationResult,
    isSimulating,
    timerCarrySimulationValue,
    showAddInput,
    showLogicSwap: showLogicSwapProps,
    conditionIndex,
    customDescription,
    uriOptionsByPosition,
  } = props
  const setSelectedIdCallback = useMakeDispatchCallback(setSelectedIdWithDrawer)
  const setNavigatorComponentMeta = useMakeDispatchCallback(setComponentMeta)

  const nodeRef = useRef(null)

  useEffect(() => {
    setNavigatorComponentMeta(uri, {
      type: SIMPLE,
      parentId: parentQueryUri,
      getRef: () => nodeRef,
      uri,
      fnId,
    })
  }, [setNavigatorComponentMeta, parentQueryUri, nodeRef, uri, fnId])

  const handleCarryIconClick = (e) => {
    e.stopPropagation()
    onCarryIconClick()
  }

  const showCarryConnector = CARRY_CONNECTOR_TYPES.has(rungType) && !carryToType
  const hasCarryType = QUERY_TYPES_WITH_EVALUATION.has(carryType)
  const showAddLogic =
    !!inputUri === false && showAddInput && rungType !== CAN_INPUT

  const isFirstConditionInLogicSet =
    parentType === COMPLEX && conditionIndex === 0

  // is single or first condition
  // no carry type
  // and has an input of some kind
  const showLogicSwap =
    showLogicSwapProps &&
    (isSingleCondition || isFirstConditionInLogicSet) &&
    isOnly &&
    hasCarryType === false &&
    (!!inputUri || !!canId) &&
    rungType !== CARRY_TIMER
  return (
    <StyledRungItem
      onClick={() => setSelectedIdCallback(uri)}
      ref={nodeRef}
      className={classNames('query-set-rung', rungType, className)}
      isSingleCondition={isSingleCondition}
      isLast={isLast}
      isFirst={isFirst}
      index={conditionIndex}
    >
      {showCarryConnector && (
        <NodeConnector rungNodeUri={uri} carryNodeUri={inputUri} fnId={fnId} />
      )}

      <StyledConditionLabel
        rungType={rungType}
        parentQueryUri={parentQueryUri}
        selectedId={selectedId}
        uri={uri}
        label={label}
        vertical={vertical}
        isLink={isLink}
        ignoreCarry={ignoreCarry}
        fnId={fnId}
        hysteresis={hysteresis}
        selectedInputDescription={selectedInputDescription}
        operator={operator}
        value={value}
        edge={edge}
        inputConfig={inputConfig}
        translationType={translationType}
        translations={translations}
        units={units}
        isSelected={selectedId === uri}
        isSixteenBitCanOutput={isSixteenBitCanOutput}
        hasErrors={hasErrors}
        isLast={isLast}
        isFirst={isFirst}
        parentType={parentType}
        subType={subType}
        carryType={carryType}
        evaluationResult={evaluationResult}
        isSimulating={isSimulating}
        timerCarrySimulationValue={timerCarrySimulationValue}
        customDescription={customDescription}
        uriOptionsByPosition={uriOptionsByPosition}
        inputUri={inputUri}
      />
      {carryToType && (
        <>
          <div className={`carry-line relation-type-${rungType}`} />
          <StyledRelationSymbol>
            <CarryTo
              carryToType={carryToType}
              onClick={handleCarryIconClick}
              highlightAffinity={highlightAffinity}
              affinityGroupNumber={affinityGroupNumber}
              data-testid={`carry-to-symbol-${carryToType}`}
            />
          </StyledRelationSymbol>
        </>
      )}
      {hasCarryType && (
        <StyledEvaluationBadge
          selected={isSelected}
          value={result}
          dataTestId={`evaluation-badge-${result ? 'T' : 'F'}`}
        />
      )}
      {hasErrors && <FeatureWarningIconStyled />}
      {showAddLogic && <QuickAddLogicMenu condition={props} />}
      {showLogicSwap && !isSingleCondition && (
        <SwapLogicSetForCondition fnId={fnId} condition={props} />
      )}
      {showLogicSwap && isSingleCondition && !isSixteenBitCanOutput && (
        <SwapConditionForLogicSet fnId={fnId} condition={props} />
      )}
    </StyledRungItem>
  )
}

Condition.defaultProps = {
  showAddInput: false,
}

Condition.propTypes = {
  uri: PropTypes.string,
  rungType: PropTypes.string,
  affinityGroupNumber: PropTypes.number,
  highlightAffinity: PropTypes.bool,
  result: PropTypes.bool,
  onCarryIconClick: PropTypes.func,
  parentBoxPosition: PropTypes.number,
  childBoxPosition: PropTypes.number,
  onRungClick: PropTypes.func,
  className: PropTypes.string,
  parentQueryUri: PropTypes.string,
  inputUri: PropTypes.string,
  fnId: PropTypes.string,
  isSelected: PropTypes.bool,
  isSixteenBitCanOutput: PropTypes.bool,
  selectedId: PropTypes.string,
  label: PropTypes.string,
  isLink: PropTypes.bool,
  vertical: PropTypes.bool,
  ignoreCarry: PropTypes.bool,
  hysteresis: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  selectedInputDescription: PropTypes.string,
  operator: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  edge: PropTypes.string,
  inputConfig: PropTypes.string,
  translationType: PropTypes.string,
  translations: PropTypes.array,
  units: PropTypes.string,
  hasErrors: PropTypes.bool,
  carryToType: PropTypes.string,
  carryType: PropTypes.string,
  isSingleCondition: PropTypes.bool,
  parentType: PropTypes.string,
  subType: PropTypes.string,
  conditionIndex: PropTypes.number,
  isLast: PropTypes.bool,
  isFirst: PropTypes.bool,
  isFirstInSet: PropTypes.bool,
  isOnly: PropTypes.bool,
  evaluationResult: PropTypes.bool,
  isSimulating: PropTypes.bool,
  timerCarrySimulationValue: PropTypes.any,
  showAddInput: PropTypes.bool,
  isEditing: PropTypes.bool,
  customDescription: PropTypes.string,
  uriOptionsByPosition: PropTypes.array,
}

export default withStyle(Condition)

export function QuickSwapConditionMenu({ overlay, label }) {
  return (
    <StyledMenuWrapper onClick={(e) => e.stopPropagation()} left={10}>
      <StyledDropdown
        size="small"
        clickHandler={() => {}}
        trigger="click"
        overlay={overlay}
        placement="bottomRight"
      >
        <Tooltip title={label}>
          <StyledCricleDiv>
            <IconSwap width={12} />
          </StyledCricleDiv>
        </Tooltip>
      </StyledDropdown>
      <svg
        width={10}
        height={22}
        viewBox={`0 0 ${30} ${30}`}
        data-testid="SVGElm"
        style={{
          position: 'absolute',
          top: 0,
          left: '22px',
        }}
      >
        <g fillRule="nonzero">
          <line
            x1="0"
            y1="50%"
            x2="100%"
            y2="50%"
            stroke="gray"
            strokeWidth="3"
          />
        </g>
      </svg>
    </StyledMenuWrapper>
  )
}

QuickSwapConditionMenu.propTypes = {
  label: PropTypes.string,
  overlay: PropTypes.element,
}

export function SwapLogicSetForCondition({ condition, fnId }) {
  const dispatch = useDispatch()
  const [showModal, updateShowModal] = useState(false)
  const [warnings, updateWarnings] = useState({})

  const labels = {
    edge: strings.edge_trigger,
    hysteresis: strings.hysteresis,
    delayDeactivate: strings.deactivate_delay,
    delayActivate: strings.activate_delay,
    gates: strings.gates,
  }

  const query = useSelector(getLogicNode, {
    queryUri: condition.parentQueryUri,
  })

  const handleConvertToSingleCondition = () => {
    // validate that this condition can be promoted
    // any delays? hysteresis? edge trigger?  or?
    // show warning
    // console.log({ condition })
    const { edge, hysteresis, gates } = condition
    const { delayActivate, delayDeactivate } = query
    const valuesToDelete = Object.entries({
      edge,
      hysteresis,
      gates,
      delayActivate,
      delayDeactivate,
    }).reduce((values, [key, value]) => {
      // console.log(`check ${key} for value: ${value}`)
      switch (key) {
        case 'hysteresis':
          if (value > 0) return { ...values, [key]: value }
          break

        case 'edge':
          if (value && value !== NO_EDGE) return { ...values, [key]: value }
          if (value === 'NoEdge') return values
          break

        case 'gates': {
          const gateValues = [...value].filter((g) => g !== 'or')
          if (gateValues.length > 0) return { ...values, [key]: gateValues }
          break
        }

        default:
          if (value && value !== 0) return { ...values, [key]: value }
      }

      return values
    }, {})
    // check the above properties and display related warnings about deletion
    if (Object.keys(valuesToDelete).length > 0) {
      updateWarnings(valuesToDelete)
      updateShowModal(true)
    } else {
      handleConvert()
    }
  }

  const handleConvert = () => {
    dispatch(moveSingleCondition(condition, fnId))
    dispatch(removeComponentMeta(condition.parentQueryUri))
    dispatch(setSelectedIdWithDrawer(condition.uri))
  }

  const overlay = (
    <Menu>
      <MenuItem key="convert" onClick={handleConvertToSingleCondition}>
        {strings.convert_to_a_single_condition}
      </MenuItem>
    </Menu>
  )

  return (
    <>
      <QuickSwapConditionMenu
        overlay={overlay}
        label={strings.this_logic_set_can_be_converted_into_a_single_condition}
      />
      {showModal && (
        <ModalSmall>
          <ModalHeader>
            <Header type="h3">
              <IconAlert />
              {strings.convert_this_logic_set}
            </Header>
            <ModalCloseButton onClick={() => updateShowModal(false)} />
          </ModalHeader>
          <ModalBody>
            <ModalMainContainer>
              <ModalContent>
                {
                  strings.some_logic_set_variables_are_incompatible_with_single_condition
                }
                <ModalList>
                  {Object.keys(warnings).map((key) => (
                    <li key={key}>{labels[key]}</li>
                  ))}
                </ModalList>
                <p>These settings will not be retained.</p>
              </ModalContent>
            </ModalMainContainer>
          </ModalBody>
          <ModalFooter>
            <ModalButton
              type="secondary"
              onClick={() => updateShowModal(false)}
            >
              {strings.cancel}
            </ModalButton>
            <ModalButton type="primary" onClick={handleConvert}>
              {strings.convert}
            </ModalButton>
          </ModalFooter>
        </ModalSmall>
      )}
    </>
  )
}

SwapLogicSetForCondition.propTypes = { ...Condition.propTypes }

export function SwapConditionForLogicSet({ condition, fnId }) {
  const dispatch = useDispatch()
  const nextLogicSetIndex = useSelector(getNewLogicSetIndex)
  const handleConvertToLogicSet = () => {
    dispatch(moveSingleConditionToLogicSet(condition, fnId, nextLogicSetIndex))
  }

  const overlay = (
    <Menu>
      <MenuItem key="convert" onClick={handleConvertToLogicSet}>
        {strings.convert_to_a_logic_set}
      </MenuItem>
    </Menu>
  )

  return (
    <QuickSwapConditionMenu
      overlay={overlay}
      label={strings.this_single_condition_can_be_converted_to_a_logic_set}
    />
  )
}

SwapConditionForLogicSet.propTypes = { ...Condition.propTypes }

export const ModalList = styled.ul`
  padding: 0 23px;
  margin: 12px 0;
`
